// client/src/components/pages/LexiStar/Reports/reportLogic.ts
//
// Pure read-side logic of the report pages (ST-00188 M4-R2). Nothing here
// computes a KPI (m4-contracts §1 "figures are deterministic"): the engine, the
// cost model and the semantic layer own every number; these helpers only pick,
// group, order and label what the APIs returned, and restate formulas with the
// record's own inputs for "how was this calculated?".

import { isKnownTimeZone } from '../../../../utils/timeZones';
import { eventTypeLabel } from '../../../../utils/eventTypeLabels';
import type {
  CostLine,
  JsonSchema,
  Loose,
  ProviderReport,
  ProviderTable,
  ReportChartSpec,
  ReportColumn,
  ReportDefinition,
  ReportFormat,
  ReportScope,
  ScheduleDraft,
  TripLeg,
} from '../../../../types/lexistar-reports';

// ── small helpers ───────────────────────────────────────────────────────────

export const isNum = (v: unknown): boolean =>
  v !== null && v !== undefined && v !== '' && typeof v !== 'boolean' && Number.isFinite(Number(v));
export const numOrNull = (v: unknown): number | null => (isNum(v) ? Number(v) : null);
const str = (v: unknown): string | null =>
  v === null || v === undefined || v === '' ? null : String(v);
const list = <T = Loose>(v: unknown): T[] => (Array.isArray(v) ? (v as T[]) : []);
const obj = (v: unknown): Loose => (v && typeof v === 'object' && !Array.isArray(v) ? (v as Loose) : {});
const msOf = (v: unknown): number => {
  if (v === null || v === undefined || v === '') return NaN;
  if (typeof v === 'number') return v < 1e12 ? v * 1000 : v;
  return Date.parse(String(v));
};

/** 'km_loaded' / 'kmLoaded' → 'Km loaded'. */
export function labelFromKey(key: string): string {
  const s = String(key)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_.]+/g, ' ')
    .trim()
    .toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** "TRIP.ARRIVED_SOURCE" → "Arrived source". */
export function kindLabel(kind: unknown): string {
  if (!kind) return '';
  const k = String(kind)
    .replace(/^[A-Z]+\./, '')
    .replace(/_/g, ' ')
    .toLowerCase();
  return k.charAt(0).toUpperCase() + k.slice(1);
}

// ── Trip report: plans (both plans on a re-planned trip) ───────────────────

export interface PlanLeg {
  seq: number | null;
  kind: string;
  loadState: string;
  from: string;
  to: string;
  route: string | null;
  plannedKm: number | null;
  budgetL: number | null;
  state: string | null;
}

export interface TripPlan {
  version: number;
  current: boolean;
  legs: PlanLeg[];
  plannedKm: number | null;
  budgetL: number | null;
  /** When this plan was replaced (the next plan's start), and why. */
  replacedAt: string | null;
  reason: string | null;
}

const sumOrNull = (xs: Array<number | null>): number | null => {
  const n = xs.filter((x): x is number => x !== null);
  return n.length ? n.reduce((a, b) => a + b, 0) : null;
};

const planLeg = (l: TripLeg): PlanLeg => ({
  seq: numOrNull(l.seq),
  kind: str(l.kind) || 'leg',
  loadState: str(l.load_state) || (l.kind === 'loaded' ? 'loaded' : 'empty'),
  from: str(l.from_name) || '—',
  to: str(l.to_name) || '—',
  route: l.route_code
    ? `${l.route_code}${l.route_version != null ? ` v${l.route_version}` : ''}`
    : l.route_id
      ? `route${l.route_version != null ? ` v${l.route_version}` : ''}`
      : null,
  plannedKm: numOrNull(l.planned_km),
  budgetL: numOrNull(l.budget_l),
  state: str(l.state),
});

/**
 * The trip's plans, oldest first. A re-plan (m3-contracts §7.3) marks the
 * superseded legs `state='replaced'` and bumps `plan_version`; legs carrying
 * their own `plan_version` group by it. Legs replaced by a re-plan belong to the
 * earlier plan; legs done before the re-plan are shared by both.
 */
export function tripPlans(
  legs: TripLeg[],
  { planVersion, timeline = [] }: { planVersion?: unknown; timeline?: Loose[] } = {}
): TripPlan[] {
  const sorted = [...legs].sort((a, b) => (numOrNull(a.seq) ?? 0) - (numOrNull(b.seq) ?? 0));
  const replans = timeline
    .filter((t) => /REPLANNED$/.test(String(t.kind || '')))
    .sort((a, b) => msOf(a.at) - msOf(b.at));
  const reasonOf = (t: Loose | undefined): string | null => {
    if (!t) return null;
    const p = obj(t.payload);
    return str(p.reason) || (p.reason_code ? kindLabel(p.reason_code) : null);
  };

  const hasVersions = sorted.some((l) => isNum(l.plan_version));
  const current = Math.max(
    numOrNull(planVersion) ?? 1,
    ...sorted.map((l) => numOrNull(l.plan_version) ?? 1)
  );
  const replaced = sorted.filter((l) => l.state === 'replaced');
  if (!hasVersions && replaced.length === 0) {
    const pl = sorted.map(planLeg);
    return [
      {
        version: current,
        current: true,
        legs: pl,
        plannedKm: sumOrNull(pl.map((l) => l.plannedKm)),
        budgetL: sumOrNull(pl.map((l) => l.budgetL)),
        replacedAt: null,
        reason: null,
      },
    ];
  }

  const versions: number[] = hasVersions
    ? [...new Set(sorted.map((l) => numOrNull(l.plan_version) ?? 1))].sort((a, b) => a - b)
    : [Math.max(1, current - 1), current];
  if (!versions.includes(current)) versions.push(current);

  return versions.map((v, i) => {
    let own: TripLeg[];
    if (hasVersions) {
      // A leg belongs to its own version and to every later one it was not replaced in.
      own = sorted.filter((l) => {
        const lv = numOrNull(l.plan_version) ?? 1;
        if (lv === v) return true;
        return lv < v && l.state !== 'replaced';
      });
    } else if (v === current) {
      own = sorted.filter((l) => l.state !== 'replaced');
    } else {
      // The earlier plan: its replaced legs plus the legs already done before the re-plan.
      const firstReplacedSeq = Math.min(...replaced.map((l) => numOrNull(l.seq) ?? Infinity));
      own = sorted.filter(
        (l) => l.state === 'replaced' || (numOrNull(l.seq) ?? Infinity) < firstReplacedSeq
      );
    }
    const pl = own.map(planLeg);
    const replan = replans[i];
    return {
      version: v,
      current: v === current,
      legs: pl,
      plannedKm: sumOrNull(pl.map((l) => l.plannedKm)),
      budgetL: sumOrNull(pl.map((l) => l.budgetL)),
      replacedAt: v === current ? null : str(replan?.at) || str(own.find((l) => l.replaced_at)?.replaced_at),
      reason: v === current ? null : reasonOf(replan),
    };
  });
}

// ── Trip report: time by stage, dwell per site, stops and idle ─────────────

export const STAGE_LABELS: Record<string, string> = {
  DISPATCHED: 'Dispatched, not acknowledged',
  ACKNOWLEDGED: 'Acknowledged',
  EN_ROUTE_TO_SOURCE: 'To source (empty)',
  AT_SOURCE: 'At source',
  IN_TRANSIT_LOADED: 'In transit (loaded)',
  AT_DESTINATION: 'At destination',
  IN_TRANSIT_EMPTY: 'Returning (empty)',
  EXCEPTION_HOLD: 'Exception hold',
};

export interface StageDuration {
  stage: string;
  label: string;
  seconds: number;
}

/**
 * Seconds per stage: from the replay's stage bands when there are any (the
 * engine's own spans), else from consecutive timeline entries that name a stage.
 */
export function stageDurations(stageBands: Loose[], timeline: Loose[] = []): StageDuration[] {
  const acc = new Map<string, number>();
  const add = (stage: string, s: number) => {
    if (!stage || !Number.isFinite(s) || s <= 0) return;
    acc.set(stage, (acc.get(stage) || 0) + s);
  };
  if (stageBands.length) {
    stageBands.forEach((b) => add(String(b.stage || ''), (msOf(b.to) - msOf(b.from)) / 1000));
  } else {
    const KIND_STAGE: Record<string, string> = {
      DISPATCHED: 'DISPATCHED',
      ACKNOWLEDGED: 'ACKNOWLEDGED',
      ARRIVED_SOURCE: 'AT_SOURCE',
      DEPARTED_SOURCE: 'IN_TRANSIT_LOADED',
      ARRIVED_DESTINATION: 'AT_DESTINATION',
      DEPARTED_DESTINATION: 'IN_TRANSIT_EMPTY',
    };
    const rows = timeline
      .map((t) => ({ kind: String(t.kind || '').replace(/^[A-Z]+\./, ''), t: msOf(t.at) }))
      .filter((r) => Number.isFinite(r.t))
      .sort((a, b) => a.t - b.t);
    rows.forEach((r, i) => {
      const stage = KIND_STAGE[r.kind];
      const next = rows[i + 1];
      if (stage && next) add(stage, (next.t - r.t) / 1000);
    });
  }
  const order = Object.keys(STAGE_LABELS);
  return [...acc.entries()]
    .sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]))
    .map(([stage, seconds]) => ({ stage, label: STAGE_LABELS[stage] || kindLabel(stage), seconds }));
}

export interface SiteDwell {
  site: string;
  purpose: string | null;
  seconds: number;
  visits: number;
  from: number;
}

const durationOf = (r: Loose): number | null => {
  const d = numOrNull(r.duration_s ?? r.dwell_s);
  if (d !== null) return d;
  const s = (msOf(r.to ?? r.exited_at) - msOf(r.from ?? r.entered_at)) / 1000;
  return Number.isFinite(s) && s >= 0 ? s : null;
};

const placeOf = (r: Loose): string =>
  str(r.name) ||
  str(r.geofence_name) ||
  str(obj(r.geofence).name) ||
  (typeof r.geofence === 'string' ? r.geofence : null) ||
  str(r.place) ||
  'Unknown place';

/** Dwell per site from the replay's geofence visits (engine dwell_s), in visit order. */
export function dwellBySite(visits: Loose[]): SiteDwell[] {
  const bySite = new Map<string, SiteDwell>();
  visits.forEach((v) => {
    const site = placeOf(v);
    const secs = durationOf(v);
    if (secs === null) return;
    const cur = bySite.get(site);
    if (cur) {
      cur.seconds += secs;
      cur.visits += 1;
    } else {
      bySite.set(site, {
        site,
        purpose: str(v.purpose_type ?? v.purpose),
        seconds: secs,
        visits: 1,
        from: msOf(v.from ?? v.entered_at),
      });
    }
  });
  return [...bySite.values()].sort((a, b) => a.from - b.from);
}

export interface StopRow {
  at: number;
  seconds: number;
  place: string;
  inZone: boolean;
}

export interface StopsIdleSummary {
  stops: number;
  stoppedSeconds: number;
  idles: number;
  idleSeconds: number;
  idleOutsideSeconds: number;
  longest: StopRow[];
}

/**
 * Stops and idle periods from the replay. "Outside known zones" = an idle
 * period the engine placed in no geofence (LS-09 §5.5 "outside permitted zones").
 */
export function stopsIdleSummary(stops: Loose[], idles: Loose[], top = 5): StopsIdleSummary {
  const rows = (xs: Loose[]): StopRow[] =>
    xs
      .map((x) => ({
        at: msOf(x.from ?? x.start ?? x.started_at),
        seconds: durationOf(x) ?? 0,
        place: placeOf(x),
        inZone: !!(x.geofence || x.geofence_id || x.geofence_name),
      }))
      .filter((r) => r.seconds > 0);
  const s = rows(stops);
  const i = rows(idles);
  return {
    stops: s.length,
    stoppedSeconds: s.reduce((a, r) => a + r.seconds, 0),
    idles: i.length,
    idleSeconds: i.reduce((a, r) => a + r.seconds, 0),
    idleOutsideSeconds: i.filter((r) => !r.inZone).reduce((a, r) => a + r.seconds, 0),
    longest: [...s].sort((a, b) => b.seconds - a.seconds).slice(0, top),
  };
}

// ── Trip report: exceptions ─────────────────────────────────────────────────

export interface ExceptionItem {
  key: string;
  text: string;
  /** Outcome / state word shown beside it (approved, rejected, open, breach …). */
  state: string;
  /** Needs attention (unapproved deviation, breach, missing presence …). */
  attention: boolean;
}

/**
 * Event types that are exceptions on a trip report. Their wording is the shared label catalogue's
 * (utils/eventTypeLabels, ST-00280: one set of words for every page, notification and message).
 */
export const EXCEPTION_EVENT_TYPES: readonly string[] = [
  'MOTION.OVERSPEED',
  'MOTION.EXCESSIVE_IDLE',
  'GEOFENCE.DWELL_EXCEEDED',
  'GEOFENCE.NOGO_ENTERED',
  'GEOFENCE.AFTER_HOURS',
  'ROUTE.STOP_WHILE_DEVIATING',
  'ROUTE.PROPOSED_ROUTE_USED',
  'ROUTE.DEVIATION_SLA_BREACHED',
  'FUEL.LOSS_SUSPECTED',
  'FUEL.REFUEL_UNAUTHORISED_LOCATION',
  'WB.BYPASS',
  'WB.UNMATCHED',
  'DEVICE.NO_SIGNAL',
  'TRIP.NOT_ACKNOWLEDGED',
  'TRIP.DELAY_AT_SITE',
  'TRIP.ETA_RISK',
  'TRIP.EXCEPTION_HOLD',
];
const isExceptionEvent = (type: string): boolean => EXCEPTION_EVENT_TYPES.includes(type);

/** Event severity (LS-04 §7: I · L · M · H · C) in words. */
const SEVERITY_WORDS: Record<string, string> = { I: 'info', L: 'low', M: 'medium', H: 'high', C: 'critical' };

/** Status flags (route inconsistencies) and the events that say the same thing. */
const FLAG_EVENT: Record<string, string> = {
  distance_above_route_norm: 'TRIP.DISTANCE_ABOVE_ROUTE_NORM',
  not_acknowledged: 'TRIP.NOT_ACKNOWLEDGED',
  eta_risk: 'TRIP.ETA_RISK',
};

const fmt = (n: number | null, digits = 0) =>
  n === null
    ? '—'
    : n.toLocaleString('en-GB', { minimumFractionDigits: digits, maximumFractionDigits: digits });

export function tripExceptions({
  trip,
  legs,
  deviations,
  matches,
  events,
  timeLabel,
}: {
  trip: Loose;
  legs: TripLeg[];
  deviations: Loose[];
  matches: Loose[];
  events: Loose[];
  timeLabel: (iso: unknown) => string;
}): ExceptionItem[] {
  const items: ExceptionItem[] = [];
  deviations.forEach((d) => {
    const state = String(d.state || 'OPEN');
    const reason = str(d.reason_code) || str(obj(d.driver_reason).code);
    items.push({
      key: `dev-${String(d.id)}`,
      text: `Deviation ${timeLabel(d.opened_at)}–${d.closed_at ? timeLabel(d.closed_at) : 'open'} · ${fmt(numOrNull(d.off_route_km), 1)} km off route${reason ? ` · ${kindLabel(reason)}` : ''}${isNum(d.extra_km_approved) ? ` · +${fmt(numOrNull(d.extra_km_approved))} km approved` : ''}`,
      state: state.toLowerCase().replace(/_/g, ' '),
      attention: !['APPROVED', 'CLOSED', 'VOIDED'].includes(state),
    });
  });

  const seen = new Set<string>();
  const byType = new Map<string, Loose[]>();
  events.forEach((e) => {
    const type = String(e.type || '');
    if (!isExceptionEvent(type)) return;
    if (String(e.status || '') === 'superseded' || String(e.status || '') === 'dismissed') return;
    byType.set(type, [...(byType.get(type) || []), e]);
  });
  byType.forEach((evs, type) => {
    seen.add(type);
    let text = `${eventTypeLabel(type)}${evs.length > 1 ? ` ×${evs.length}` : ''}`;
    if (type === 'MOTION.OVERSPEED') {
      const max = Math.max(...evs.map((e) => numOrNull(obj(e.payload).max_speed_kmh) ?? -Infinity));
      const limits = evs.map((e) => numOrNull(obj(e.payload).limit_kmh)).filter((x): x is number => x !== null);
      if (Number.isFinite(max)) {
        text += ` (max ${fmt(max)} km/h${limits.length ? ` in ${fmt(Math.min(...limits))}` : ''})`;
      }
    } else if (evs.length === 1) {
      text += ` · ${timeLabel(evs[0]?.event_time ?? evs[0]?.t)}`;
    }
    items.push({ key: `ev-${type}`, text, state: SEVERITY_WORDS[String(evs[0]?.severity)] || 'event', attention: true });
  });

  legs
    .filter((l) => l.over_consumption && l.state !== 'replaced')
    .forEach((l) => {
      const oc = l.over_consumption!;
      items.push({
        key: `oc-${String(l.seq)}`,
        text: `Over-consumption on the ${l.load_state || l.kind} leg: +${fmt(numOrNull(oc.excess_l), 1)} L (${oc.certainty})`,
        state: oc.severity,
        attention: true,
      });
    });
  if (trip.variance_breach === true) {
    items.push({ key: 'var', text: 'Weighbridge variance outside tolerance', state: 'breach', attention: true });
  }
  matches
    .filter((m) => m.presence === 'absent' && m.state !== 'rejected')
    .forEach((m) =>
      items.push({
        key: `pr-${String(m.id)}`,
        text: `${kindLabel(m.ticket_type) || 'Ticket'} ticket without presence at the weighbridge`,
        state: 'check',
        attention: true,
      })
    );
  list<string>(trip.status_flags).forEach((f) => {
    if (FLAG_EVENT[f] && seen.has(FLAG_EVENT[f]!)) return;
    items.push({ key: `flag-${f}`, text: `Route inconsistency: ${kindLabel(f).toLowerCase()}`, state: 'flag', attention: false });
  });
  return items;
}

// ── Trip report: message log summary ───────────────────────────────────────

export interface MessageSummary {
  total: number;
  outbound: number;
  inbound: number;
  failed: number;
  costZar: number | null;
  byChannel: Array<{ channel: string; count: number; costZar: number | null }>;
  recent: Array<{ id: string; at: string | null; direction: string; channel: string; what: string; state: string }>;
}

export function messageSummary(messages: Loose[], recentCount = 8): MessageSummary {
  const channels = new Map<string, { count: number; cost: number | null }>();
  let cost: number | null = null;
  messages.forEach((m) => {
    const ch = String(m.channel || 'unknown');
    const c = numOrNull(obj(m.cost).amount_zar ?? m.cost_zar);
    const cur = channels.get(ch) || { count: 0, cost: null };
    cur.count += 1;
    if (c !== null) {
      cur.cost = (cur.cost ?? 0) + c;
      cost = (cost ?? 0) + c;
    }
    channels.set(ch, cur);
  });
  const dir = (m: Loose) => (String(m.direction || 'out') === 'in' ? 'in' : 'out');
  return {
    total: messages.length,
    outbound: messages.filter((m) => dir(m) === 'out').length,
    inbound: messages.filter((m) => dir(m) === 'in').length,
    failed: messages.filter((m) => ['failed', 'suppressed'].includes(String(m.state))).length,
    costZar: cost,
    byChannel: [...channels.entries()]
      .map(([channel, v]) => ({ channel, count: v.count, costZar: v.cost }))
      .sort((a, b) => b.count - a.count),
    recent: [...messages]
      .sort((a, b) => msOf(a.created_at) - msOf(b.created_at))
      .slice(-recentCount)
      .map((m, i) => ({
        id: String(m.id ?? i),
        at: str(m.created_at),
        direction: dir(m),
        channel: String(m.channel || '—'),
        what: kindLabel(m.intent || m.template_key) || (str(m.body)?.slice(0, 60) ?? '—'),
        state: String(m.state || '—'),
      })),
  };
}

export const CHANNEL_LABELS: Record<string, string> = {
  whatsapp: 'WhatsApp',
  telegram: 'Telegram',
  sms: 'SMS',
  email: 'Email',
  in_app: 'In-app',
  push: 'Push',
};
export const channelLabel = (c: string): string => CHANNEL_LABELS[c] || kindLabel(c);

// ── Cost: the rows the report prints ────────────────────────────────────────

export const COST_KIND_ORDER = ['fuel', 'running', 'toll', 'time', 'other'];
export const COST_KIND_LABELS: Record<string, string> = {
  fuel: 'Fuel',
  running: 'Running',
  toll: 'Tolls',
  time: 'Driver time',
  other: 'Other',
};

/** Cost lines summed per kind in the LS-09 order (Fuel · Running · Tolls · Driver · Other). */
export function costByKind(lines: CostLine[]): Array<{ kind: string; label: string; amount: number | null; lines: CostLine[] }> {
  return COST_KIND_ORDER.map((kind) => {
    const own = lines.filter((l) => (COST_KIND_ORDER.includes(l.kind) ? l.kind : 'other') === kind);
    const amounts = own.map((l) => l.amount_zar).filter((a): a is number => a !== null);
    return {
      kind,
      label: COST_KIND_LABELS[kind] || kindLabel(kind),
      amount: amounts.length ? amounts.reduce((a, b) => a + b, 0) : own.length ? null : 0,
      lines: own,
    };
  });
}

// ── Schedules: cadence presets ↔ cron ───────────────────────────────────────

export type CadenceKind = 'daily' | 'weekly' | 'monthly' | 'shift_end' | 'custom';

export interface Cadence {
  kind: CadenceKind;
  /** HH:MM (daily, weekly, monthly). */
  time: string;
  /** 0 = Sunday … 6 = Saturday (weekly). */
  weekday: number;
  /** 1–28 (monthly; 28 keeps every month). */
  monthday: number;
  /** HH:MM shift ends (shift_end) — one minute past the hour for all of them. */
  shiftEnds: string[];
  /** Raw five-field cron (custom). */
  cron: string;
}

export const WEEKDAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export const DEFAULT_CADENCE: Cadence = {
  kind: 'daily',
  time: '06:00',
  weekday: 1,
  monthday: 1,
  shiftEnds: ['06:00', '18:00'],
  cron: '0 6 * * *',
};

const TIME_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;
const hm = (t: string): [number, number] | null => {
  const m = TIME_RE.exec(t.trim());
  return m ? [Number(m[1]), Number(m[2])] : null;
};
const pad = (n: number) => String(n).padStart(2, '0');

/** Why this cadence cannot be saved (null when it can). */
export function cadenceError(c: Cadence): string | null {
  if (c.kind === 'custom') return cronError(c.cron);
  if (c.kind === 'shift_end') {
    const times = c.shiftEnds.map(hm);
    if (!times.length || times.some((t) => !t)) return 'Give every shift end as HH:MM.';
    const minutes = new Set(times.map((t) => t![1]));
    if (minutes.size > 1) return 'Shift ends must share the same minute (for example 06:00 and 18:00).';
    return null;
  }
  if (!hm(c.time)) return 'Give the time as HH:MM.';
  if (c.kind === 'monthly' && !(c.monthday >= 1 && c.monthday <= 28)) {
    return 'Choose a day from 1 to 28 so every month has it.';
  }
  return null;
}

export function cadenceToCron(c: Cadence): string {
  if (c.kind === 'custom') return c.cron.trim().replace(/\s+/g, ' ');
  if (c.kind === 'shift_end') {
    const times = c.shiftEnds.map(hm).filter((t): t is [number, number] => !!t);
    const minute = times[0]?.[1] ?? 0;
    const hours = [...new Set(times.map((t) => t[0]))].sort((a, b) => a - b);
    return `${minute} ${hours.join(',') || '0'} * * *`;
  }
  const [h, m] = hm(c.time) || [6, 0];
  if (c.kind === 'weekly') return `${m} ${h} * * ${c.weekday}`;
  if (c.kind === 'monthly') return `${m} ${h} ${c.monthday} * *`;
  return `${m} ${h} * * *`;
}

const FIELD_RE = /^(\*|\d+(-\d+)?)(\/\d+)?(,(\d+(-\d+)?)(\/\d+)?)*$/;

/** Five-field cron check (minute hour day-of-month month day-of-week). */
export function cronError(cron: string): string | null {
  const parts = String(cron || '').trim().split(/\s+/);
  if (parts.length !== 5) return 'A cron expression has five fields: minute hour day month weekday.';
  const bounds: Array<[number, number]> = [
    [0, 59],
    [0, 23],
    [1, 31],
    [1, 12],
    [0, 7],
  ];
  for (let i = 0; i < 5; i++) {
    const f = parts[i]!;
    if (!FIELD_RE.test(f)) return `Field ${i + 1} ("${f}") is not valid.`;
    const nums = f.match(/\d+/g)?.map(Number) || [];
    const [lo, hi] = bounds[i]!;
    const stepless = f.replace(/\/\d+/g, '').match(/\d+/g)?.map(Number) || [];
    if (stepless.some((n) => n < lo || n > hi)) return `Field ${i + 1} must be within ${lo}–${hi}.`;
    if (!nums.length && f !== '*') return `Field ${i + 1} ("${f}") is not valid.`;
  }
  return null;
}

/** Read a cron back into the preset that produces it (custom when none does). */
export function cronToCadence(cron: string): Cadence {
  const c = String(cron || '').trim().replace(/\s+/g, ' ');
  const base = { ...DEFAULT_CADENCE, cron: c || DEFAULT_CADENCE.cron };
  const m = /^(\d{1,2}) ([\d,]+) (\*|\d{1,2}) \* (\*|[0-6])$/.exec(c);
  if (!m) return { ...base, kind: 'custom' };
  const minute = Number(m[1]);
  const hours = m[2]!.split(',').map(Number);
  const dom = m[3]!;
  const dow = m[4]!;
  if (minute > 59 || hours.some((h) => h > 23)) return { ...base, kind: 'custom' };
  if (hours.length > 1) {
    if (dom !== '*' || dow !== '*') return { ...base, kind: 'custom' };
    return { ...base, kind: 'shift_end', shiftEnds: hours.map((h) => `${pad(h)}:${pad(minute)}`) };
  }
  const time = `${pad(hours[0]!)}:${pad(minute)}`;
  if (dom === '*' && dow === '*') return { ...base, kind: 'daily', time };
  if (dom === '*' && dow !== '*') return { ...base, kind: 'weekly', time, weekday: Number(dow) };
  if (dom !== '*' && dow === '*' && Number(dom) >= 1 && Number(dom) <= 28) {
    return { ...base, kind: 'monthly', time, monthday: Number(dom) };
  }
  return { ...base, kind: 'custom' };
}

const ordinal = (n: number) => {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return `${n}${s[(v - 20) % 10] || s[v] || s[0]}`;
};

/** "Daily at 06:00", "Mondays at 07:30", "Shift ends 06:00 and 18:00", or the cron. */
export function describeCron(cron: string): string {
  const c = cronToCadence(cron);
  switch (c.kind) {
    case 'daily':
      return `Daily at ${c.time}`;
    case 'weekly':
      return `${WEEKDAYS[c.weekday]}s at ${c.time}`;
    case 'monthly':
      return `Monthly on the ${ordinal(c.monthday)} at ${c.time}`;
    case 'shift_end':
      return `Shift ends ${c.shiftEnds.join(' and ')}`;
    default:
      return `Custom: ${c.cron}`;
  }
}

// ── Schedules: validation ───────────────────────────────────────────────────

export const CHANNELS: Array<{ value: 'email' | 'telegram' | 'in_app'; label: string }> = [
  { value: 'email', label: 'Email' },
  { value: 'telegram', label: 'Telegram' },
  { value: 'in_app', label: 'In-app' },
];

export const RECIPIENT_KINDS: Array<{ value: 'user' | 'contact' | 'telegram_group' | 'group'; label: string; hint?: string }> = [
  { value: 'user', label: 'A user' },
  { value: 'contact', label: 'An external contact', hint: 'name@company.co.za' },
  { value: 'telegram_group', label: 'A Telegram group', hint: '-1001234567890' },
  { value: 'group', label: 'A recipient group' },
];

/** The scope filters (ReportScope keys) a recipient override may set. */
export const SCOPE_KEYS: Array<{ value: keyof ReportScope; label: string; hint: string }> = [
  { value: 'asset_keys', label: 'Trucks', hint: 'reg:JX12KLGP, reg:HB45ZNGP' },
  { value: 'route_ids', label: 'Routes (ids)', hint: 'route UUIDs' },
  { value: 'lane_ids', label: 'Lanes (ids)', hint: 'lane UUIDs' },
  { value: 'site_ids', label: 'Sites (ids)', hint: '11, 12' },
  { value: 'customers', label: 'Customers', hint: 'Mine A, Siding B' },
  { value: 'materials', label: 'Materials', hint: 'RB3 coal' },
  { value: 'driver_ids', label: 'Drivers (ids)', hint: 'driver UUIDs' },
];

/** "a, b" → ['a', 'b'] (numbers for site ids). */
export function scopeValues(key: keyof ReportScope, text: string): Array<string | number> {
  const parts = text
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean);
  return key === 'site_ids' ? parts.filter((x) => isNum(x)).map(Number) : parts;
}

/** "Trucks: reg:JX12KLGP · Customers: Mine A". */
export function describeScope(scope: ReportScope | null | undefined): string {
  if (!scope) return '';
  return SCOPE_KEYS.filter((k) => Array.isArray(scope[k.value]) && (scope[k.value] as unknown[]).length)
    .map((k) => `${k.label.replace(/ \(ids\)$/, '')}: ${(scope[k.value] as unknown[]).join(', ')}`)
    .join(' · ');
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const TELEGRAM_RE = /^-?\d{5,}$|^@[A-Za-z0-9_]{5,}$/;

export function scheduleErrors(d: ScheduleDraft, cadence: Cadence): string[] {
  const errors: string[] = [];
  if (!d.name.trim()) errors.push('Give the schedule a name.');
  if (!d.report_key) errors.push('Choose a report.');
  const ce = cadenceError(cadence);
  if (ce) errors.push(ce);
  // ST-00273: an IANA zone from the list (a stored typed value may be none).
  if (!isKnownTimeZone(d.tz)) errors.push('Choose a time zone from the list.');
  if (!d.formats.length) errors.push('Choose at least one format.');
  if (!d.channels.length) errors.push('Choose at least one channel.');
  if (!d.recipients.length) errors.push('Add at least one recipient.');
  d.recipients.forEach((r, i) => {
    const n = i + 1;
    if (!String(r.ref || '').trim()) errors.push(`Recipient ${n} needs a value.`);
    else if (r.kind === 'contact' && !EMAIL_RE.test(r.ref.trim()) && !/^\+?\d{9,15}$/.test(r.ref.trim())) {
      errors.push(`Recipient ${n}: give an email address or a phone number.`);
    } else if (r.kind === 'telegram_group' && !TELEGRAM_RE.test(r.ref.trim())) {
      errors.push(`Recipient ${n}: give the Telegram chat id (e.g. -1001234567890) or @name.`);
    }
    if (r.scope && !Object.values(r.scope).some((v) => Array.isArray(v) && v.length)) {
      errors.push(`Recipient ${n}: give the scope a value or remove it.`);
    }
    if (r.kind === 'telegram_group' && !d.channels.includes('telegram')) {
      errors.push(`Recipient ${n} is a Telegram group — tick the Telegram channel.`);
    }
  });
  return errors;
}

// ── Parameter forms from report_definitions.parameters (JSON schema) ────────

export type FieldKind = 'date' | 'daterange' | 'datetime' | 'select' | 'multiselect' | 'number' | 'boolean' | 'text';

export interface FormField {
  name: string;
  /** daterange: the two property names it writes. */
  names: string[];
  kind: FieldKind;
  label: string;
  description: string | null;
  required: boolean;
  options: Array<{ value: string; label: string }>;
  lookup: string | null;
  relative: string[];
  default: unknown;
  min: number | null;
  max: number | null;
  /** JSON-schema format of a text field (uuid …). */
  format: string | null;
  /** A string array without an enum — edited as a comma list. */
  array: boolean;
}

const RANGE_PAIRS: Array<[string, string]> = [
  ['from', 'to'],
  ['date_from', 'date_to'],
  ['start', 'end'],
  ['period_start', 'period_end'],
];

const typeOf = (s: JsonSchema): string => (Array.isArray(s.type) ? s.type.find((t) => t !== 'null') || 'string' : s.type || 'string');

/** The form fields of a parameter schema, in declaration order (from/to pairs → one range). */
export function schemaFields(schema: JsonSchema | null | undefined): FormField[] {
  const props = schema?.properties || {};
  const required = new Set(schema?.required || []);
  const used = new Set<string>();
  const fields: FormField[] = [];
  const base = (name: string, s: JsonSchema): Omit<FormField, 'kind'> => ({
    name,
    names: [name],
    label: s.title || labelFromKey(name),
    description: s.description || null,
    required: required.has(name),
    options: [],
    lookup: s['x-lookup'] || null,
    relative: s['x-relative'] || [],
    default: s.default,
    min: typeof s.minimum === 'number' ? s.minimum : null,
    max: typeof s.maximum === 'number' ? s.maximum : null,
    format: s.format || null,
    array: false,
  });
  Object.entries(props).forEach(([name, s]) => {
    if (used.has(name)) return;
    const pair = RANGE_PAIRS.find(([a]) => a === name);
    const other = pair ? props[pair[1]] : undefined;
    if (pair && other && s.format === 'date' && other.format === 'date') {
      used.add(pair[0]);
      used.add(pair[1]);
      fields.push({
        ...base(name, s),
        kind: 'daterange',
        names: [pair[0], pair[1]],
        label: 'Period',
        required: required.has(pair[0]) || required.has(pair[1]),
        relative: [...new Set([...(s['x-relative'] || []), ...(other['x-relative'] || [])])],
      });
      return;
    }
    used.add(name);
    const t = typeOf(s);
    const enumOptions = (e: Array<string | number>, names?: string[]) =>
      e.map((v, i) => ({ value: String(v), label: names?.[i] || labelFromKey(String(v)) }));
    if (t === 'array') {
      const items = s.items || {};
      fields.push({
        ...base(name, s),
        kind: items.enum ? 'multiselect' : 'text',
        options: items.enum ? enumOptions(items.enum, items.enumNames || items['x-enumNames']) : [],
        array: !items.enum,
      });
    } else if (s.enum) {
      fields.push({ ...base(name, s), kind: 'select', options: enumOptions(s.enum, s.enumNames || s['x-enumNames']) });
    } else if (t === 'boolean') {
      fields.push({ ...base(name, s), kind: 'boolean' });
    } else if (t === 'integer' || t === 'number') {
      fields.push({ ...base(name, s), kind: 'number' });
    } else if (s.format === 'date') {
      fields.push({ ...base(name, s), kind: 'date' });
    } else if (s.format === 'date-time') {
      fields.push({ ...base(name, s), kind: 'datetime' });
    } else {
      fields.push({ ...base(name, s), kind: 'text' });
    }
  });
  return fields;
}

/** Schema defaults as a parameter object. */
export function defaultParams(schema: JsonSchema | null | undefined): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  Object.entries(schema?.properties || {}).forEach(([k, s]) => {
    if (s.default !== undefined) out[k] = s.default;
  });
  return out;
}

/**
 * Parameters from a query string, typed by the schema (unknown keys dropped),
 * plus a relative `period` the providers resolve (yesterday, last_week …).
 */
export function paramsFromSearch(
  schema: JsonSchema | null | undefined,
  search: URLSearchParams
): Record<string, unknown> {
  const out: Record<string, unknown> = defaultParams(schema);
  const period = search.get('period');
  if (period && RELATIVE_PERIODS.some((p) => p.value === period)) out.period = period;
  Object.entries(schema?.properties || {}).forEach(([k, s]) => {
    const raw = search.get(k);
    if (raw === null || raw === '') return;
    const t = typeOf(s);
    if (t === 'array') out[k] = raw.split(',').filter(Boolean);
    else if (t === 'integer' || t === 'number') out[k] = isNum(raw) ? Number(raw) : raw;
    else if (t === 'boolean') out[k] = raw === 'true' || raw === '1';
    else out[k] = raw;
  });
  return out;
}

/** Parameters → query string entries (arrays comma-joined, empties dropped). */
export function paramsToSearch(params: Record<string, unknown>): Record<string, string> {
  const out: Record<string, string> = {};
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === '') return;
    if (Array.isArray(v)) {
      if (v.length) out[k] = v.join(',');
    } else out[k] = String(v);
  });
  return out;
}

/** Required parameters that are missing (field labels). */
export function missingParams(fields: FormField[], params: Record<string, unknown>): string[] {
  const relative = typeof params.period === 'string' && params.period !== '';
  return fields
    .filter((f) => f.required)
    // A relative period stands in for fixed dates (the provider resolves it).
    .filter((f) => !(relative && (f.kind === 'daterange' || f.kind === 'date')))
    .filter((f) =>
      f.names.some((n) => {
        const v = params[n];
        return v === undefined || v === null || v === '' || (Array.isArray(v) && !v.length);
      })
    )
    .map((f) => f.label);
}

/** Relative periods a schedule stores in `parameters.period` (M4-R1 providers/shared.ts PERIODS). */
export const RELATIVE_PERIODS: Array<{ value: string; label: string }> = [
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'today', label: 'Today so far' },
  { value: 'last_7_days', label: 'The last 7 days' },
  { value: 'last_week', label: 'Last week (Mon–Sun)' },
  { value: 'this_week', label: 'This week so far' },
  { value: 'last_30_days', label: 'The last 30 days' },
  { value: 'last_month', label: 'Last month' },
  { value: 'this_month', label: 'This month so far' },
];
export const relativeLabel = (v: unknown): string =>
  RELATIVE_PERIODS.find((p) => p.value === v)?.label || labelFromKey(String(v));

// ── Where a definition's page lives ─────────────────────────────────────────

/** Dedicated pages: report key → the parameter that goes into the path. */
export const DEDICATED_PAGES: Record<string, { path: string; param: string; label: string; lookup: string }> = {
  trip_report: { path: '/lexistar/reports/trip/:trip_id', param: 'trip_id', label: 'Trip', lookup: 'trip' },
  run_summary: { path: '/lexistar/reports/run/:run_id', param: 'run_id', label: 'Run', lookup: 'run' },
  daily_management_summary: { path: '/lexistar/reports/daily/:date', param: 'date', label: 'Day', lookup: 'date' },
  trip_reconciliation_certificate: {
    path: '/lexistar/commissioning/:parallel_run_id/certificate',
    param: 'parallel_run_id',
    label: 'Parallel run',
    lookup: 'parallel_run',
  },
};

/** The client route that shows `def` for `params` (null when a path parameter is missing). */
export function pageFor(def: Pick<ReportDefinition, 'key' | 'page'>, params: Record<string, unknown> = {}): string | null {
  const template = def.page || DEDICATED_PAGES[def.key]?.path || null;
  if (!template) {
    const q = new URLSearchParams(paramsToSearch(params)).toString();
    return `/lexistar/reports/${encodeURIComponent(def.key)}${q ? `?${q}` : ''}`;
  }
  let missing = false;
  const consumed = new Set<string>();
  const path = template.replace(/:([a-z_]+)/g, (_, name: string) => {
    consumed.add(name);
    const v = params[name];
    if (v === undefined || v === null || v === '') {
      missing = true;
      return `:${name}`;
    }
    return encodeURIComponent(String(v));
  });
  if (missing) return null;
  const rest = Object.fromEntries(Object.entries(params).filter(([k]) => !consumed.has(k)));
  const q = new URLSearchParams(paramsToSearch(rest)).toString();
  return `${path}${q ? `?${q}` : ''}`;
}

export const FORMAT_LABELS: Record<ReportFormat, string> = { pdf: 'PDF', xlsx: 'XLSX', csv: 'CSV', html: 'HTML' };

// ── Standard reports: provider data → tables + chart ───────────────────────

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DATETIME_RE = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function inferKind(key: string, rows: Loose[]): string {
  const vals = rows.map((r) => r[key]).filter((v) => v !== null && v !== undefined && v !== '');
  if (!vals.length) return 'text';
  if (/provenance$/.test(key)) return 'provenance';
  if (vals.every((v) => typeof v === 'boolean')) return 'boolean';
  if (vals.every((v) => typeof v === 'string' && DATE_RE.test(v))) return 'date';
  if (vals.every((v) => typeof v === 'string' && DATETIME_RE.test(v))) return 'datetime';
  if (vals.every((v) => typeof v === 'number' || (typeof v === 'string' && isNum(v)))) {
    if (/(_s|_seconds)$/.test(key)) return 'duration_s';
    if (/_pct$/.test(key)) return 'percent';
    return 'number';
  }
  return 'text';
}

const UNIT_SUFFIX: Array<[RegExp, string]> = [
  [/_km$/, 'km'],
  [/_l$/, 'L'],
  [/_kg$/, 'kg'],
  [/_t$|tonnes/, 't'],
  [/_zar$|_rand$/, 'ZAR'],
  [/_h$|_hours$/, 'h'],
  [/_min$/, 'min'],
];

export function inferColumns(rows: Loose[]): ReportColumn[] {
  const keys: string[] = [];
  rows.slice(0, 50).forEach((r) =>
    Object.keys(r).forEach((k) => {
      if (!keys.includes(k) && !/^(id|tenant_id|drill|_.*)$/.test(k)) keys.push(k);
    })
  );
  return keys.map((key) => {
    const kind = inferKind(key, rows);
    const unit = kind === 'number' ? UNIT_SUFFIX.find(([re]) => re.test(key))?.[1] || null : null;
    return { key, label: labelFromKey(key.replace(/_(km|l|kg|t|zar|pct|h|min)$/, '')), unit, kind };
  });
}

const NUMERIC = ['number', 'integer', 'duration_s'];

/** A bar/line chart when a table has a date or category axis and numeric series of one unit. */
export function inferChart(columns: ReportColumn[], rows: Loose[]): ReportChartSpec | null {
  if (rows.length < 2 || rows.length > 62) return null;
  const x =
    columns.find((c) => c.kind === 'date') ||
    columns.find((c) => c.kind === 'text' && new Set(rows.map((r) => r[c.key])).size === rows.length);
  if (!x) return null;
  const numeric = columns.filter((c) => NUMERIC.includes(c.kind) && !/(_id|_seq|^seq)$/.test(c.key));
  const first = numeric[0];
  if (!first) return null;
  const sameUnit = numeric.filter((c) => (c.unit ?? null) === (first.unit ?? null) && c.kind === first.kind).slice(0, 3);
  return {
    type: x.kind === 'date' ? 'line' : 'bar',
    x: x.key,
    series: sameUnit.map((c) => ({ key: c.key, label: c.label, unit: c.unit ?? null })),
  };
}

function normaliseColumns(cols: Loose[], rows: Loose[]): ReportColumn[] {
  if (!cols.length) return inferColumns(rows);
  return cols.map((c) => {
    const key = String(c.key ?? c.field ?? c.name);
    return {
      key,
      label: str(c.label ?? c.title) || labelFromKey(key),
      kind: str(c.type ?? c.kind) || inferKind(key, rows),
      unit: str(c.unit),
      decimals: numOrNull(c.decimals),
    };
  });
}

function normaliseTable(t: Loose, i: number): ProviderTable {
  const rows = list<Loose>(t.rows ?? t.items ?? t.data);
  const columns = normaliseColumns(list<Loose>(t.columns), rows);
  const chartSpec = obj(t.chart);
  return {
    key: str(t.key) || `table-${i}`,
    title: str(t.title),
    columns,
    rows,
    totals: t.totals && typeof t.totals === 'object' ? (t.totals as Loose) : null,
    primary: t.primary === true,
    chart:
      chartSpec.x && list(chartSpec.series).length
        ? {
            type: chartSpec.type === 'line' ? 'line' : 'bar',
            x: String(chartSpec.x),
            series: list<Loose>(chartSpec.series).map((s) => ({
              key: String(s.key),
              label: str(s.label) || labelFromKey(String(s.key)),
              unit: str(s.unit),
            })),
          }
        : t.chart === false
          ? null
          : inferChart(columns, rows),
  };
}

/**
 * Normalise a provider's `data` (M4-R1 ReportData: headline + tables + notes +
 * provisional) — or a bare row list — into what the page prints. The primary
 * table (the one the CSV carries) comes first.
 */
export function normaliseProviderData(data: unknown, pinned: Loose | null = null): ProviderReport {
  const d = obj(data);
  const tablesRaw: Loose[] = Array.isArray(data)
    ? [{ rows: data }]
    : list<Loose>(d.tables).length
      ? list<Loose>(d.tables)
      : list(d.rows ?? d.items).length
        ? [{ rows: d.rows ?? d.items, columns: d.columns, totals: d.totals, chart: d.chart, primary: true }]
        : [];
  const tables = tablesRaw.map(normaliseTable).sort((a, b) => Number(b.primary) - Number(a.primary));
  const period = obj(d.period);
  const state = str(d.state);
  return {
    title: str(d.title),
    subtitle: str(d.subtitle),
    period: period.from && period.to ? { from: String(period.from), to: String(period.to), label: str(period.label) } : null,
    headline: list<Loose>(d.headline).map((h) => ({
      key: String(h.key ?? h.label),
      label: str(h.label) || labelFromKey(String(h.key)),
      value: (h.value as number | string | null) ?? null,
      unit: str(h.unit),
      provenance: str(h.provenance),
      state: h.state === 'final' || h.state === 'provisional' ? h.state : null,
      metric_key: str(h.metric_key),
      drill: str(h.drill),
    })),
    tables,
    notes: list<unknown>(d.notes).map(String),
    state: state === 'final' || state === 'provisional' ? state : d.provisional === true ? 'provisional' : d.provisional === false ? 'final' : null,
    pinned: pinned || (d.pinned && typeof d.pinned === 'object' ? (d.pinned as Loose) : null),
    generatedAt: str(d.generated_at),
  };
}
